/**
 * Accessibility Service - Runtime WCAG 2.1 AA compliance scanning
 * Uses axe-core for automated accessibility testing
 */

export interface AccessibilityViolation {
  id: string;
  impact: 'minor' | 'moderate' | 'serious' | 'critical';
  description: string;
  help: string;
  helpUrl: string;
  nodes: Array<{
    html: string;
    target: string[];
    failureSummary: string;
  }>;
}

export interface AccessibilityResult {
  url: string;
  name: string;
  timestamp: Date;
  violations: AccessibilityViolation[];
  passes: number;
  incomplete: number;
  score: number;
}

export interface AccessibilityReport {
  generatedAt: Date;
  overallScore: number;
  totalViolations: number;
  criticalCount: number;
  seriousCount: number;
  moderateCount: number;
  minorCount: number;
  pageResults: AccessibilityResult[];
  pagesScanned: number;
}

// Routes to test for accessibility
export const TESTABLE_ROUTES = [
  { path: '/', name: 'Home' },
  { path: '/login', name: 'Login' },
  { path: '/artists', name: 'Artists Directory' },
  { path: '/sessions', name: 'Sessions' },
  { path: '/playlists', name: 'Playlists' },
  { path: '/albums', name: 'Albums' },
  { path: '/mission', name: 'Mission' },
  { path: '/regions', name: 'Regions' },
  { path: '/account', name: 'Account' },
  { path: '/favorites', name: 'Favorites' },
];

// WCAG 2.1 AA rules we care about
export const WCAG_AA_TAGS = [
  'wcag2a',
  'wcag2aa',
  'wcag21a',
  'wcag21aa',
  'best-practice',
];

class AccessibilityService {
  private axe: any = null;
  private isLoaded = false;

  async loadAxe(): Promise<void> {
    if (this.isLoaded) return;

    try {
      // Dynamically import axe-core for runtime scanning
      const axeCore = await import('axe-core');
      this.axe = axeCore.default || axeCore;
      this.isLoaded = true;
    } catch (error) {
      console.error('Failed to load axe-core:', error);
      throw new Error('Accessibility scanner not available');
    }
  }

  async scanCurrentPage(name?: string): Promise<AccessibilityResult> {
    await this.loadAxe();

    const results = await this.axe.run(document, {
      runOnly: {
        type: 'tag',
        values: WCAG_AA_TAGS,
      },
    });

    const violations: AccessibilityViolation[] = results.violations.map((v: any) => ({
      id: v.id,
      impact: v.impact,
      description: v.description,
      help: v.help,
      helpUrl: v.helpUrl,
      nodes: v.nodes.map((n: any) => ({
        html: n.html,
        target: n.target,
        failureSummary: n.failureSummary,
      })),
    }));

    const totalChecks = results.passes.length + results.violations.length + results.incomplete.length;
    const score = totalChecks > 0
      ? Math.round((results.passes.length / totalChecks) * 100)
      : 100;

    return {
      url: window.location.pathname,
      name: name || window.location.pathname,
      timestamp: new Date(),
      violations,
      passes: results.passes.length,
      incomplete: results.incomplete.length,
      score,
    };
  }

  // Store results in localStorage for multi-page scanning
  private readonly STORAGE_KEY = 'accessibility-scan-results';
  private readonly SCAN_STATE_KEY = 'accessibility-scan-state';

  // Save a scan result to localStorage
  saveResult(result: AccessibilityResult): void {
    const existing = this.getSavedResults();
    // Replace if same URL exists, otherwise add
    const filtered = existing.filter(r => r.url !== result.url);
    filtered.push(result);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
  }

  // Get all saved results
  getSavedResults(): AccessibilityResult[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return [];
      const parsed = JSON.parse(data);
      // Restore Date objects
      return parsed.map((r: any) => ({
        ...r,
        timestamp: new Date(r.timestamp),
      }));
    } catch {
      return [];
    }
  }

  // Clear saved results
  clearSavedResults(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.SCAN_STATE_KEY);
  }

  // Get/set scan state for multi-page navigation
  getScanState(): { routes: Array<{ path: string; name: string }>; currentIndex: number } | null {
    try {
      const data = localStorage.getItem(this.SCAN_STATE_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  setScanState(routes: Array<{ path: string; name: string }>, currentIndex: number): void {
    localStorage.setItem(this.SCAN_STATE_KEY, JSON.stringify({ routes, currentIndex }));
  }

  clearScanState(): void {
    localStorage.removeItem(this.SCAN_STATE_KEY);
  }

  // Check if we're in the middle of a multi-page scan
  isMultiPageScanInProgress(): boolean {
    return this.getScanState() !== null;
  }

  // Start a new multi-page scan (stores state and navigates to first page)
  startMultiPageScan(routes: Array<{ path: string; name: string }>): void {
    this.clearSavedResults();
    this.setScanState(routes, 0);
    // Navigate to first route
    window.location.href = routes[0].path + '?a11y-scan=true';
  }

  // Continue the multi-page scan (call this on page load when scan is in progress)
  async continueMultiPageScan(): Promise<{ done: boolean; result?: AccessibilityResult; progress?: { current: number; total: number; name: string } }> {
    const state = this.getScanState();
    if (!state) {
      return { done: true };
    }

    const { routes, currentIndex } = state;
    const currentRoute = routes[currentIndex];

    // Scan the current page
    const result = await this.scanCurrentPage(currentRoute.name);
    result.url = currentRoute.path; // Use the route path, not the actual URL which may have query params
    this.saveResult(result);

    const nextIndex = currentIndex + 1;

    if (nextIndex >= routes.length) {
      // All done - clear state
      this.clearScanState();
      return {
        done: true,
        result,
        progress: { current: nextIndex, total: routes.length, name: currentRoute.name },
      };
    }

    // Update state and navigate to next page
    this.setScanState(routes, nextIndex);
    const nextRoute = routes[nextIndex];

    // Small delay before navigation to allow UI to update
    setTimeout(() => {
      window.location.href = nextRoute.path + '?a11y-scan=true';
    }, 500);

    return {
      done: false,
      result,
      progress: { current: currentIndex + 1, total: routes.length, name: currentRoute.name },
    };
  }

  // Get the final report from saved results
  getMultiPageReport(): AccessibilityReport | null {
    const results = this.getSavedResults();
    if (results.length === 0) return null;
    return this.generateReport(results);
  }

  generateReport(results: AccessibilityResult[]): AccessibilityReport {
    const allViolations = results.flatMap(r => r.violations);

    const criticalCount = allViolations.filter(v => v.impact === 'critical').length;
    const seriousCount = allViolations.filter(v => v.impact === 'serious').length;
    const moderateCount = allViolations.filter(v => v.impact === 'moderate').length;
    const minorCount = allViolations.filter(v => v.impact === 'minor').length;

    // Only count pages with successful scans for score
    const validResults = results.filter(r => r.passes > 0 || r.violations.length > 0);
    const overallScore = validResults.length > 0
      ? Math.round(validResults.reduce((sum, r) => sum + r.score, 0) / validResults.length)
      : 0;

    return {
      generatedAt: new Date(),
      overallScore,
      totalViolations: allViolations.length,
      criticalCount,
      seriousCount,
      moderateCount,
      minorCount,
      pageResults: results,
      pagesScanned: results.length,
    };
  }

  getImpactColor(impact: string): string {
    switch (impact) {
      case 'critical': return '#d32f2f';
      case 'serious': return '#f57c00';
      case 'moderate': return '#fbc02d';
      case 'minor': return '#388e3c';
      default: return '#757575';
    }
  }

  // WCAG 1.4.3 (SON-24): these tints colour the score icon + the large "%"
  // <h2> on /accessibility, both drawn on a white card. The previous amber
  // (#fbc02d ≈ 1.6:1) and orange (#f57c00 ≈ 2.9:1) failed contrast. Darkened
  // so every tier clears ≥4.5:1 on white while staying recognisably
  // green/amber/orange/red.
  getScoreColor(score: number): string {
    // Each clears ≥4.5:1 on the white (light) card and ≥3:1 on the dark card
    // (#1e1e1e) for the large 48px "%" <h2>, passing AA in both themes.
    if (score >= 90) return '#2e7d32'; // 5.1:1 white / 3.3:1 dark
    if (score >= 70) return '#a86700'; // 4.6:1 white / 3.7:1 dark
    if (score >= 50) return '#c2410c'; // 5.2:1 white / 3.2:1 dark
    return '#d32f2f';                  // 5.0:1 white / 3.4:1 dark
  }
}

export const accessibilityService = new AccessibilityService();
